import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
type NavDropdownProps = {
  label: string;
  items: {
    label: string;
    to: string;
  }[];
  footer?: {
    label: string;
    to: string;
  };
  active?: boolean;
};
export function NavDropdown({
  label,
  items,
  footer,
  active
}: NavDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number>();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);
  const enter = () => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const leave = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };
  return <div ref={ref} className="relative" onMouseEnter={enter} onMouseLeave={leave} data-id="element-214" data-mp="buggoegmtftu">
      <button type="button" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen(v => !v)} className={`flex h-10 items-center gap-1 whitespace-nowrap text-sm transition-colors duration-150 ease-smooth hover:text-mauve ${active ? 'font-medium text-mauve' : 'text-ink/75'}`} data-id="element-215" data-mp="6x6tz6kmni9p">
        {label}
        <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-150 ease-smooth ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-216" data-mp="3k2yjvmpvkwun" />
      </button>
      <AnimatePresence data-id="element-217" data-mp="1n8aqidouvr14">
        {open ? <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className="absolute left-1/2 top-full z-50 w-64 -translate-x-1/2 pt-2" data-id="element-218" data-mp="1icn2w2q0v5xq">
            <div className="overflow-hidden rounded-xl border border-petal bg-white py-2 shadow-[0_12px_32px_rgba(46,36,48,0.12)]" data-id="element-219" data-mp="32uxijlcb6duo">
              {items.map(item => <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="block px-4 py-2.5 text-sm text-ink/80 transition-colors duration-150 ease-smooth hover:bg-petal/50 hover:text-mauve-deep" data-id="element-220" data-mp="3vi0h2dgziokx">
                  {item.label}
                </Link>)}
              {footer ? <Link to={footer.to} onClick={() => setOpen(false)} className="mt-1 block border-t border-petal px-4 pb-1 pt-3 text-sm font-medium text-mauve hover:text-mauve-deep" data-id="element-221" data-mp="2b26x85w5y9k7">
                  {footer.label} →
                </Link> : null}
            </div>
          </motion.div> : null}
      </AnimatePresence>
    </div>;
}